import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Check, X, Trash2, ArrowRight, ShoppingCart, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SERVICES, SERVICE_MAP, CATEGORIES, BILLING, taxConfig } from "@/data/pricing";
import { computeTotals, computeServicePrice, fmt } from "@/lib/pricingEngine";

const INCLUDED = { en: "Included", ar: "مشمول" };

export function PackageBuilder({ initial, initKey }) {
  const { t, isAr } = useLang();
  const navigate = useNavigate();
  const [selection, setSelection] = useState(() => ({ ...initial }));
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [annualContract, setAnnualContract] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSelection({ ...initial }); }, [initKey]); // reset on preset change

  const isSel = (id) => id in selection;

  const add = (id) => {
    setSelection((prev) => {
      const next = { ...prev };
      const s = SERVICE_MAP[id];
      (s.excludes || []).forEach((x) => { delete next[x]; });
      next[id] = prev[id] || {};
      return next;
    });
  };
  const remove = (id) => setSelection((prev) => { const n = { ...prev }; delete n[id]; return n; });
  const toggle = (id) => (isSel(id) ? remove(id) : add(id));
  const setWl = (id, key, val) =>
    setSelection((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const totals = useMemo(() => computeTotals(selection, { annualContract }), [selection, annualContract]);
  const selectedCount = Object.keys(selection).length;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICES.filter((s) => {
      if (cat !== "all" && s.category !== cat) return false;
      if (!q) return true;
      return (s.name.en + s.name.ar + s.desc.en + s.desc.ar).toLowerCase().includes(q);
    });
  }, [query, cat]);

  const cardPriceLabel = (s) => {
    if (s.alwaysIncluded || totals.includedFree.has(s.id) || (s.freeWith12mo && annualContract && isSel(s.id)))
      return { text: t(INCLUDED), free: true };
    const variable = s.model === "tiered" || s.model === "custom" || s.countKey;
    const price = computeServicePrice(s, selection[s.id] || {});
    const prefix = variable && !isSel(s.id) ? (isAr ? "من " : "From ") : "";
    return { text: prefix + fmt(price) + " " + t(BILLING[s.billing]), free: false };
  };

  const requestPackage = () => {
    const L = [];
    const section = (arr, title, totalLabel, total) => {
      if (!arr.length) return;
      L.push("", title);
      arr.forEach((i) => L.push(`• ${i.name.en}${i.name.ar ? " / " + i.name.ar : ""}: ${i.included ? "Included" : fmt(i.price) + " " + BILLING[i.billing].en}`));
      L.push(`${totalLabel}: ${fmt(total)}`);
    };
    section(totals.items.monthly, "MONTHLY SERVICES", "MONTHLY TOTAL", totals.monthlyTotal);
    section(totals.items.onetime, "ONE-TIME SERVICES", "ONE-TIME TOTAL", totals.onetimeTotal);
    section(totals.items.annual, "ANNUAL SERVICES", "ANNUAL TOTAL", totals.annualTotal);
    if (annualContract) L.push("", "12-month accounting contract selected (setup free).");
    sessionStorage.setItem("sav_pkg", JSON.stringify({
      service: "Build Your Own Package",
      message: L.join("\n").trim(),
    }));
    navigate("/#contact");
  };

  const catChip = (id, label) => (
    <button key={id} onClick={() => setCat(id)}
      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
        cat === id ? "bg-navy text-white" : "bg-white border border-line text-muted hover:border-navy/40 hover:text-navy"
      }`}>{label}</button>
  );

  // ---- Live summary panel (shared desktop + mobile drawer) ----
  const SummaryBody = () => {
    const Section = ({ arr, title, total, totalLabel }) => arr.length ? (
      <div className="mb-5">
        <div className="text-[0.7rem] font-bold uppercase tracking-wider text-muted mb-2">{title}</div>
        <div className="space-y-1.5">
          {arr.map((i) => (
            <div key={i.id} className="flex items-start justify-between gap-3 text-sm">
              <span className="text-ink/80 leading-snug">{t(i.name)}</span>
              <span className={`shrink-0 font-semibold ${i.included ? "text-brandgreen" : "text-ink"}`} dir="ltr">
                {i.included ? t(INCLUDED) : fmt(i.price)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2.5 pt-2.5 border-t border-line flex items-center justify-between">
          <span className="text-sm font-bold text-navy">{totalLabel}</span>
          <span className="font-display text-lg text-navy" dir="ltr">{fmt(total)}</span>
        </div>
      </div>
    ) : null;
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-navy">{t({ en: "Your Package", ar: "باقتك" })}</h3>
          <span className="text-xs font-semibold bg-crimson-soft text-crimson-600 px-2.5 py-1 rounded-full">
            {selectedCount} {t({ en: "selected", ar: "مختار" })}
          </span>
        </div>
        {selectedCount === 0 && (
          <p className="text-sm text-muted py-6 text-center">{t({ en: "Add services to see your live price.", ar: "أضف خدمات لرؤية السعر مباشرةً." })}</p>
        )}
        <Section arr={totals.items.monthly} title={t({ en: "Monthly Services", ar: "خدمات شهرية" })} total={totals.monthlyTotal} totalLabel={t({ en: "Monthly Total", ar: "الإجمالي الشهري" })} />
        <Section arr={totals.items.onetime} title={t({ en: "One-Time Services", ar: "خدمات لمرة واحدة" })} total={totals.onetimeTotal} totalLabel={t({ en: "One-Time Total", ar: "إجمالي مرة واحدة" })} />
        <Section arr={totals.items.annual} title={t({ en: "Annual Services", ar: "خدمات سنوية" })} total={totals.annualTotal} totalLabel={t({ en: "Annual Total", ar: "الإجمالي السنوي" })} />
        <label className="flex items-center gap-2.5 mt-1 mb-4 cursor-pointer text-sm text-muted">
          <input type="checkbox" checked={annualContract} onChange={(e) => setAnnualContract(e.target.checked)} className="h-4 w-4 accent-navy" />
          {t({ en: "12-month accounting contract (setup free)", ar: "عقد محاسبة ١٢ شهراً (الإعداد مجاناً)" })}
        </label>
        <button onClick={requestPackage} disabled={selectedCount === 0} data-testid="request-package"
          className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-crimson text-white font-semibold hover:bg-crimson-600 transition-colors disabled:opacity-50">
          {t({ en: "Request this package", ar: "اطلب هذه الباقة" })} <ArrowRight className="h-4 w-4" />
        </button>
        {taxConfig.displayMode === "inclusive" && (
          <p className="text-[0.7rem] text-muted text-center mt-3">{t({ en: "Prices include 5% VAT.", ar: "الأسعار تشمل ضريبة القيمة المضافة ٥٪." })}</p>
        )}
      </div>
    );
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start w-full min-w-0">
      {/* -------- Catalogue -------- */}
      <div className="min-w-0">
        <div className="relative mb-5">
          <Search className="absolute top-1/2 -translate-y-1/2 start-4 h-5 w-5 text-muted" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="service-search"
            placeholder={t({ en: "Search services (VAT, bank, payroll, audit…)", ar: "ابحث عن خدمة (ضريبة، بنك، رواتب، تدقيق…)" })}
            className="w-full h-12 ps-12 pe-4 rounded-full border border-line bg-white text-ink placeholder:text-muted/60 focus:outline-none focus:border-navy focus:ring-4 focus:ring-navy/10 transition-[border-color,box-shadow]" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1 no-scrollbar">
          {catChip("all", t({ en: "All", ar: "الكل" }))}
          {CATEGORIES.map((c) => catChip(c.id, t(c.label)))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 min-w-0">
          {filtered.map((s) => {
            const sel = isSel(s.id);
            const pl = cardPriceLabel(s);
            return (
              <div key={s.id} data-testid={`service-${s.id}`}
                className={`rounded-2xl border p-5 flex flex-col transition-all duration-200 ${
                  sel ? "border-navy bg-navy/[0.03] shadow-card" : "border-line bg-white hover:border-navy/30 hover:shadow-card"
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-ink leading-snug">{t(s.name)}</h4>
                  {sel && <Check className="h-5 w-5 text-brandgreen shrink-0" />}
                </div>
                <p className="mt-1.5 text-sm text-muted leading-snug flex-1">{t(s.desc)}</p>
                <div className={`mt-3 font-display text-lg ${pl.free ? "text-brandgreen" : "text-navy"}`} dir="ltr">{pl.text}</div>

                {sel && s.inputs.length > 0 && (
                  <div className="mt-3 grid gap-2.5 rounded-xl bg-surface p-3">
                    {s.inputs.map((inp) => (
                      <label key={inp.key} className="text-xs text-muted">
                        <span className="block mb-1">{t(inp.label)}</span>
                        <input type="number" min={inp.min} step={inp.step}
                          value={selection[s.id]?.[inp.key] ?? inp.def}
                          onChange={(e) => setWl(s.id, inp.key, e.target.value === "" ? "" : Math.max(inp.min, Number(e.target.value)))}
                          className="w-full h-9 px-3 rounded-lg border border-line bg-white text-ink text-sm focus:outline-none focus:border-navy" dir="ltr" />
                      </label>
                    ))}
                  </div>
                )}

                <div className="mt-4">
                  {sel ? (
                    <button onClick={() => remove(s.id)} data-testid={`remove-${s.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full border border-crimson/40 text-crimson font-semibold text-sm hover:bg-crimson hover:text-white transition-colors">
                      <Trash2 className="h-4 w-4" /> {t({ en: "Remove", ar: "إزالة" })}
                    </button>
                  ) : (
                    <button onClick={() => add(s.id)} data-testid={`add-${s.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-navy text-white font-semibold text-sm hover:bg-navy-600 transition-colors">
                      <Plus className="h-4 w-4" /> {t({ en: "Add service", ar: "أضف الخدمة" })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-muted col-span-full py-8 text-center">{t({ en: "No services match your search.", ar: "لا توجد خدمات مطابقة." })}</p>
          )}
        </div>
      </div>

      {/* -------- Desktop sticky summary -------- */}
      <aside className="hidden lg:block sticky top-28">
        <div className="rounded-2xl border border-line bg-white shadow-lift p-6">
          <SummaryBody />
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-muted px-2">
          <ShieldCheck className="h-4 w-4 text-navy" /> {t({ en: "No hidden fees. Every service is calculable.", ar: "لا رسوم خفية. كل خدمة قابلة للحساب." })}
        </div>
      </aside>

      {/* -------- Mobile sticky bottom bar -------- */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-line shadow-lift px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-[0.7rem] text-muted">{t({ en: "Monthly total", ar: "الإجمالي الشهري" })}</div>
          <div className="font-display text-lg text-navy" dir="ltr">{fmt(totals.monthlyTotal)}</div>
        </div>
        <button onClick={() => setShowSummary(true)} data-testid="mobile-view-package"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-crimson text-white font-semibold text-sm">
          <ShoppingCart className="h-4 w-4" /> {t({ en: "View package", ar: "عرض الباقة" })} ({selectedCount})
        </button>
      </div>
      {showSummary && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="mobile-summary">
          <div className="absolute inset-0 bg-navy-deep/50" onClick={() => setShowSummary(false)} />
          <div className="relative w-full max-h-[85vh] overflow-y-auto bg-white rounded-t-3xl p-6">
            <button onClick={() => setShowSummary(false)} className="absolute top-4 end-4 h-9 w-9 grid place-items-center rounded-full bg-surface"><X className="h-5 w-5" /></button>
            <SummaryBody />
          </div>
        </div>
      )}
    </div>
  );
}
